import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { hd } from './helpdesk.typings';
import { XDeviceService } from './xdevice.service';
import { IMCConnectionInfo, McConnectionsService } from './mc-connections/mc-connections.service';
import { IDeviceContract } from './device-formatter.service';
import { Enums } from './enums/enums';
import { map } from 'rxjs/operators';

type IDeviceModel = hd.IDeviceModel;

export interface IDeviceIdToDeviceMap {
  [deviceId: string]: IDeviceModel;
}

@Injectable()
export class DeviceContextService {
  private _devices: IDeviceIdToDeviceMap = {};
  private _deviceContracts: IDeviceContract[];
  private deviceInfoDictionary: { [deviceId: string]: Observable<any> } = {};
  private _selectedDevice: string = null;
  private _defaultMcConnectionId: number;

  constructor(private deviceInfoService: XDeviceService, private mcConnectionsService: McConnectionsService) {}

  public getDeviceInfo = (deviceInfoId: string, refreshCache = false): Observable<hd.IDeviceModel> => {
    let deviceInfoObservable = this.deviceInfoDictionary[deviceInfoId];

    if (!deviceInfoObservable || refreshCache) {
      console.log('retrieving device info', deviceInfoId);
      deviceInfoObservable = this.deviceInfoService
        .getDevices(deviceInfoId)
        .pipe(map(devices => devices.find(d => d.mcDeviceId === deviceInfoId)));
      this.deviceInfoDictionary[deviceInfoId] = deviceInfoObservable;

      // deviceInfoPromise.catch((error: hd.IError) => {
      //     this.$log.debug('Failed to lookup user info for id: ' + deviceInfoId);
      //     this.$log.error(error);
      // });
    }

    return deviceInfoObservable;
  };

  public get devices(): IDeviceIdToDeviceMap {
    return this._devices;
  }

  public get devicesSsp(): IDeviceContract[] {
    return this._deviceContracts;
  }

  public set devicesSsp(devices: IDeviceContract[]) {
    this._deviceContracts = devices;
  }

  public convertMcDevices = (mcDevices: IDeviceContract[]): Promise<IDeviceIdToDeviceMap> => {
    return new Promise((resolve, reject) => {
      if (!this._defaultMcConnectionId) {
        this.getDefaultMcConnectionId().then((defaultMcConnectionId: number) => {
          this._defaultMcConnectionId = defaultMcConnectionId;
          this.initializeDeviceModels(mcDevices);
          resolve(this._devices);
        });
      } else {
        this.initializeDeviceModels(mcDevices);
        resolve(this._devices);
      }
    });
  };

  public clearDevices = () => {
    this._devices = {};
  };

  public get selectedDevice(): string {
    return this._selectedDevice;
  }

  public set selectedDevice(deviceId: string) {
    this._selectedDevice = deviceId;
  }

  private getDefaultMcConnectionId = (): Promise<number> => {
    return new Promise<number>((resolve, reject) => {
      this.mcConnectionsService.get().subscribe((mcConnections: IMCConnectionInfo[]) => {
        let defaultMcConnectionId = mcConnections.find(c => c.isDefault).id;
        resolve(defaultMcConnectionId);
      });
    });
  };

  private getDeviceId = (mcDeviceId: string): string => {
    return 'eMC' + this._defaultMcConnectionId + '-' + mcDeviceId;
  };

  private initializeDeviceModels = (mcDevices: IDeviceContract[]) => {
    let device: IDeviceContract;
    for (device of mcDevices) {
      this._devices[device._obj.deviceId] = this.initializeDeviceModel(device);
    }
  };

  private initializeDeviceModel = (mcDevice: IDeviceContract): IDeviceModel => {
    const device: IDeviceModel = <IDeviceModel>{
      deviceId: this.getDeviceId(mcDevice._obj.deviceId),
      deviceName: mcDevice._obj.deviceName,
      kind: mcDevice._obj.kind,
      family: Enums.DeviceFamily[mcDevice.family],
      osVersion: mcDevice._obj.osVersion,
      model: mcDevice._obj.model,
      macAddress: mcDevice._obj.macAddress,
      phoneNumber: mcDevice._obj.phoneNumber,
      hardwareSerialNumber: mcDevice._obj.hardwareSerialNumber,
      manufacturer: mcDevice._obj.manufacturer,
    };
    return device;
  };
}

export const DEVICE_OS_FAMILY = {
  Apple: 'Apple',
  Android: 'Android',
  AndroidPlus: 'AndroidPlus',
  WindowsCE: 'WindowsCE',
  WindowsDesktop: 'WindowsDesktop',
  WindowsModernDesktop: 'WindowsModernDesktop',
  WindowsPhone: 'WindowsPhone',
  WindowsRuntime: 'WindowsRuntime',
  Scanner: 'Scanner',
  Blackberry: 'Blackberry',
  Printer: 'Printer',
  Linux: 'Linux',
  Tizen: 'Tizen',
};
